import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';

import { company } from '~/company/Company';
import { cfSrcForImage } from '~/components/CategoryBlocks/utils';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';

import { Button } from '../Button';
import type { ProductCarouselMobileProps } from '../Carousel/interfaces';
import { CategoryBlockTitle } from '../CategoryBlocks/shared/CategoryBlockTitle';
import { BackgroundLayer } from './BackgroundLayer';

/**
 * Mobile product carousel.
 *
 * `background` is rendered as a layer behind the content rather than as a CSS
 * background so the block can size it responsively. That layer needs `isolate`
 * on the root: without a stacking context its negative z-index would put it
 * below the root's own background colour instead of above it. The layer clips
 * itself rather than the root, so content that outgrows a fixed `aspect-*` box
 * still overflows as it did before.
 *
 * While loading, the skeleton replaces only the row of products: the title, the
 * view-all control and the spacing around them are known before the products
 * are, and rendering them only afterwards shifted everything below the block by
 * their height.
 */
const ProductCarouselMobile = ({
  title,
  skeletonComponent,
  items,
  itemClassName,
  linkTo,
  categoryId,
  showViewAllOnMobile = false,
  viewAllVariant = 'header',
  background,
  headerImage,
  intro,
  style,
  className,
  ...props
}: ProductCarouselMobileProps) => {
  const { t } = useTranslation();
  const { handleClickCategory } = useCategoryAnalytics();

  const showViewAllHeader =
    showViewAllOnMobile && viewAllVariant === 'header' && !!linkTo;
  const showViewAllBottom =
    showViewAllOnMobile && viewAllVariant === 'bottom' && !!linkTo;

  const isLoading = !!skeletonComponent && items.length === 0;

  return (
    <div
      {...props}
      style={style}
      className={cn(
        'flex h-auto w-full flex-col',
        company.match({
          mcosmetic: 'gap-6',
          default: 'gap-4',
        }),
        background && 'relative isolate',
        className,
      )}
    >
      {background && <BackgroundLayer>{background}</BackgroundLayer>}
      {intro}
      <div className="flex items-center justify-between empty:hidden">
        {title && viewAllVariant !== 'bottom' && (
          <CategoryBlockTitle>{title}</CategoryBlockTitle>
        )}
        {showViewAllHeader && (
          <Link to={linkTo} className="shrink-0 no-underline">
            <Button
              variant="outlined"
              size="XS"
              onClick={categoryId ? handleClickCategory(categoryId) : undefined}
              className={
                headerImage &&
                "relative before:absolute before:top-0 before:left-0 before:z-[-1] before:h-full before:w-full before:bg-bg-content before:content-['']"
              }
            >
              {t('viewAll')}
              {headerImage && (
                <div
                  style={{
                    backgroundImage: `url(${cfSrcForImage(headerImage)})`,
                  }}
                  className="absolute -top-[25px] -left-[37px] -z-2 h-[68px] w-[133px] bg-contain bg-top bg-no-repeat"
                />
              )}
            </Button>
          </Link>
        )}
      </div>
      <div
        className={cn(
          '-mx-4 flex max-w-[calc(100%+32px)] gap-2 overflow-x-auto overflow-y-hidden px-4 pb-2',
          '[-ms-overflow-style:none] [-webkit-overflow-scrolling:touch] [scrollbar-width:none]',
          '[&::-webkit-scrollbar]:hidden',
        )}
      >
        {isLoading
          ? skeletonComponent
          : items.map((item) => (
              <div key={item.id} className={cn('flex shrink-0', itemClassName)}>
                {item.node}
              </div>
            ))}
      </div>
      {showViewAllBottom && (
        <Link to={linkTo} className="shrink-0 no-underline">
          <Button
            size="L"
            className="w-full border-graphic-regular-white! bg-graphic-regular-white!"
          >
            <Typography.Text300Semibold as="span" className="text-text-brand">
              {t('viewAll')} {title}
            </Typography.Text300Semibold>
          </Button>
        </Link>
      )}
    </div>
  );
};

export default ProductCarouselMobile;
